import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { Moment } from 'moment';

import {
	WalletOperationCriteria,
	WalletOperationKind,
	emptyWalletOperationCriteria,
	zWalletOperationKind,
} from 'lib/models/walletOperations';

import useWalletOperationLabels from 'hooks/useWalletOperationLabels';

import FilterAccordion from 'components/FilterAccordion';

type Props = {
	onSearch: (criteria: WalletOperationCriteria) => void;
	loading?: boolean;
};

/** What "no filter" is worth in a MUI select, which cannot hold undefined. */
const ALL = '';

/**
 * What the operator typed, before it is a search.
 *
 * The amounts are kept as the strings they were typed as rather than as numbers: "12," is a
 * thing somebody types on the way to "12,50" and is not a number yet, and a field that
 * rewrote itself while it was being filled would be unusable.
 */
type Draft = {
	kind: WalletOperationKind | '';
	subject: string;
	actor: string;
	amountMin: string;
	amountMax: string;
	from: Moment | null;
	to: Moment | null;
};

const emptyDraft: Draft = {
	kind: ALL,
	subject: '',
	actor: '',
	amountMin: '',
	amountMax: '',
	from: null,
	to: null,
};

/**
 * The kind a select was left on, or no kind at all.
 *
 * The value is read back through the schema rather than asserted: the endpoint refuses a
 * kind it cannot read, so anything the enum does not name is "every kind" here.
 */
function readKind(value: unknown): WalletOperationKind | '' {
	const parsed = zWalletOperationKind.safeParse(value);
	return parsed.success ? parsed.data : ALL;
}

/**
 * One amount field, as a number the API would accept, or nothing.
 *
 * An empty field is not a zero: the endpoint reads `amountMin=0` as a real bound and would
 * answer a different question from the one nobody asked.
 *
 * @param value What was typed
 */
function readAmount(value: string): number | undefined {
	const trimmed = value.trim();
	if (!trimmed) return undefined;

	const amount = Number(trimmed.replace(',', '.'));

	return Number.isFinite(amount) && amount >= 0 ? amount : undefined;
}

/** Whether an amount field holds something that is not an amount. */
function isBrokenAmount(value: string): boolean {
	return !!value.trim() && readAmount(value) === undefined;
}

/**
 * What support narrows the listing by: who, how much and when.
 *
 * Nothing is sent while it is being typed. The usernames are matched exactly by the API, so
 * a request per keystroke would be a hundred answers of "no hay operaciones" to a question
 * half asked; the search leaves as a whole when the operator says so.
 *
 * The two dates are days on screen and instants on the wire: a day with no offset is read in
 * UTC by the endpoint, and the person asking means the Saturday they live in, so each day is
 * sent as the pair of instants that bound it here.
 */
export default function WalletOperationsFilters({ onSearch, loading }: Props) {
	const { t } = useTranslation();
	const { kindLabel } = useWalletOperationLabels();

	const [draft, setDraft] = useState<Draft>(emptyDraft);

	const amountMin = readAmount(draft.amountMin);
	const amountMax = readAmount(draft.amountMax);

	// A picker being typed into holds a date that is not one yet, and a moment that is not a
	// date has no instant to send.
	const brokenDate = !!(draft.from && !draft.from.isValid()) || !!(draft.to && !draft.to.isValid());

	// Why the search cannot be sent as it stands, which is what the panel says under the
	// fields. The endpoint refuses each of these with a 400, and a refusal the operator can
	// only read as "no hay operaciones" is worth saying here first.
	const broken = isBrokenAmount(draft.amountMin)
		? t('common:walletOperationsAmountInvalid')
		: isBrokenAmount(draft.amountMax)
			? t('common:walletOperationsAmountInvalid')
			: amountMin !== undefined && amountMax !== undefined && amountMin > amountMax
				? t('common:walletOperationsAmountRangeInvalid')
				: brokenDate
					? t('common:walletOperationsDateInvalid')
					: draft.from && draft.to && draft.from.isAfter(draft.to, 'day')
						? t('common:walletOperationsDateRangeInvalid')
						: '';

	function search(next: Draft) {
		const { from, to } = next;
		const min = readAmount(next.amountMin);
		const max = readAmount(next.amountMax);

		onSearch({
			...(next.kind ? { kind: next.kind } : {}),
			...(next.subject.trim() ? { subject: next.subject.trim() } : {}),
			...(next.actor.trim() ? { actor: next.actor.trim() } : {}),
			...(min !== undefined ? { amountMin: min } : {}),
			...(max !== undefined ? { amountMax: max } : {}),
			// Cloned before being moved: moment mutates in place, and a picker whose value
			// silently became midnight is a date the operator did not choose.
			...(from?.isValid() ? { from: from.clone().startOf('day').toISOString() } : {}),
			...(to?.isValid() ? { to: to.clone().endOf('day').toISOString() } : {}),
		});
	}

	function clear() {
		setDraft(emptyDraft);
		onSearch(emptyWalletOperationCriteria);
	}

	return (
		<FilterAccordion
			title={t('common:walletOperationsFilters')}
			onClear={clear}
			clearTooltip={t('common:walletOperationsFiltersClear')}
			defaultExpanded={false}
		>
			<Box
				component="form"
				onSubmit={(event) => {
					event.preventDefault();
					if (!broken) search(draft);
				}}
			>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							value={draft.subject}
							label={t('common:walletOperationsFilterSubject')}
							helperText={t('common:walletOperationsFilterUsernameHelp')}
							onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							value={draft.actor}
							label={t('common:walletOperationsFilterActor')}
							helperText={t('common:walletOperationsFilterUsernameHelp')}
							onChange={(event) => setDraft({ ...draft, actor: event.target.value })}
						/>
					</Grid>

					<Grid item xs={6} sm={3} md={1.5}>
						<TextField
							fullWidth
							value={draft.amountMin}
							label={t('common:walletOperationsFilterAmountMin')}
							error={isBrokenAmount(draft.amountMin)}
							onChange={(event) => setDraft({ ...draft, amountMin: event.target.value })}
						/>
					</Grid>

					<Grid item xs={6} sm={3} md={1.5}>
						<TextField
							fullWidth
							value={draft.amountMax}
							label={t('common:walletOperationsFilterAmountMax')}
							error={isBrokenAmount(draft.amountMax)}
							onChange={(event) => setDraft({ ...draft, amountMax: event.target.value })}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<DatePicker
							sx={{ width: '100%' }}
							value={draft.from}
							label={t('common:walletOperationsFilterFrom')}
							format="DD/MM/YYYY"
							onChange={(value) => setDraft({ ...draft, from: value })}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<DatePicker
							sx={{ width: '100%' }}
							value={draft.to}
							minDate={draft.from || undefined}
							label={t('common:walletOperationsFilterTo')}
							format="DD/MM/YYYY"
							onChange={(value) => setDraft({ ...draft, to: value })}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<FormControl fullWidth>
							<InputLabel id="wallet-operation-kind">{t('common:walletOperationKind')}</InputLabel>
							<Select
								labelId="wallet-operation-kind"
								id="wallet-operation-kind-select"
								label={t('common:walletOperationKind')}
								value={draft.kind}
								onChange={(event) => setDraft({ ...draft, kind: readKind(event.target.value) })}
							>
								<MenuItem value={ALL}>{t('common:walletOperationsFilterAll')}</MenuItem>
								{zWalletOperationKind.options.map((option) => (
									<MenuItem key={option} value={option}>
										{kindLabel(option)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					<Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Button type="submit" variant="contained" disabled={!!broken || loading}>
							{t('common:search')}
						</Button>
						<Button onClick={clear} disabled={loading}>
							{t('common:walletOperationsFiltersClearAction')}
						</Button>
					</Grid>

					<Grid item xs={12}>
						<Typography variant="caption" color={broken ? 'error' : 'text.secondary'}>
							{broken || t('common:walletOperationsFiltersHelp')}
						</Typography>
					</Grid>
				</Grid>
			</Box>
		</FilterAccordion>
	);
}
